'use client'

import {useEffect, useMemo, useRef} from 'react'
import {captureException, logClientWarn, sanitizeClientDiagnosticText} from '../../lib/client-logger'
import {useAuth} from '@fitro/customer-host/auth'
import {getPortalRootDiagnostics} from '../../lib/portal-root'

type RejectionContext = {
    reasonType: string
    reasonName: string | null
    reasonCode: string | number | null
    reasonPreview: string | null
    reasonIsError: boolean
    reasonHasStack: boolean
}

const resolvePageVisibility = (): string | null => {
    if (typeof document === 'undefined') return null
    return typeof document.visibilityState === 'string' ? document.visibilityState : null
}

const resolveOnlineStatus = (): boolean | null => {
    if (typeof navigator === 'undefined' || typeof navigator.onLine !== 'boolean') return null
    return navigator.onLine
}

const getRenderTimingNow = () => {
    if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
        return performance.now()
    }
    return Date.now()
}

const resolveDocumentLanguage = (): string | null => {
    if (typeof document === 'undefined') return null
    return document.documentElement.lang || null
}

const resolveTranslateMode = (target: HTMLElement | null): string | null => {
    if (!target) return null
    return target.getAttribute('translate')
}

const resolveTranslationArtifactPresence = (): boolean | null => {
    if (typeof document === 'undefined') return null

    return Boolean(
        document.querySelector(
            '.skiptranslate, .translated-ltr, .translated-rtl, .goog-te-banner-frame, iframe.goog-te-banner-frame'
        )
    )
}

const resolveActiveElementTag = (): string | null => {
    if (typeof document === 'undefined') return null
    const activeElement = document.activeElement
    return activeElement instanceof HTMLElement ? activeElement.tagName.toLowerCase() : null
}

const resolveActiveElementRole = (): string | null => {
    if (typeof document === 'undefined') return null
    const activeElement = document.activeElement
    if (!(activeElement instanceof HTMLElement)) return null
    return activeElement.getAttribute('role')
}

const resolveDocumentUiState = () => {
    if (typeof document === 'undefined') {
        return {
            documentTitle: null,
            bodyNoScroll: null,
            htmlNoScroll: null,
            activeDialogCount: null,
            activeElementTag: null,
            activeElementRole: null,
        }
    }

    return {
        documentTitle: document.title || null,
        bodyNoScroll: document.body.classList.contains('no-scroll'),
        htmlNoScroll: document.documentElement.classList.contains('no-scroll'),
        activeDialogCount: document.querySelectorAll('[role="dialog"]').length,
        activeElementTag: resolveActiveElementTag(),
        activeElementRole: resolveActiveElementRole(),
    }
}

const buildSharedClientContext = () => {
    const htmlElement = typeof document !== 'undefined' ? document.documentElement : null
    const bodyElement = typeof document !== 'undefined' ? document.body : null

    return {
        ...resolveDocumentUiState(),
        ...getPortalRootDiagnostics(),
        document_lang: resolveDocumentLanguage(),
        html_translate: resolveTranslateMode(htmlElement),
        body_translate: resolveTranslateMode(bodyElement),
        translation_artifact_present: resolveTranslationArtifactPresence(),
        active_element_tag: resolveActiveElementTag(),
        active_element_role: resolveActiveElementRole(),
    }
}

type AuthDiagnosticsSource = {
    userId?: string | null
    orgId?: string | null
    sessionId?: string | null
    isLoaded?: boolean
    isSignedIn?: boolean
}

const buildAuthDiagnostics = (auth: AuthDiagnosticsSource): Record<string, string | boolean | null> => ({
    authUserId: typeof auth.userId === 'string' && auth.userId.trim() ? auth.userId : null,
    authOrgId: typeof auth.orgId === 'string' && auth.orgId.trim() ? auth.orgId : null,
    authSessionId: typeof auth.sessionId === 'string' && auth.sessionId.trim() ? auth.sessionId : null,
    authIsLoaded: typeof auth.isLoaded === 'boolean' ? auth.isLoaded : null,
    authIsSignedIn: typeof auth.isSignedIn === 'boolean' ? auth.isSignedIn : null,
})

const resolveDomMutationSignal = (error: unknown, filename?: string | null): string | null => {
    if (!(error instanceof Error)) return null

    const code = (error as {code?: unknown}).code
    const isNotFoundDomError = error.name === 'NotFoundError' || code === 8 || code === '8'
    if (isNotFoundDomError) return 'notfound_dom'

    if (error.name === 'HierarchyRequestError' || error.name === 'InvalidStateError') {
        return 'dom_tree_state'
    }

    if (error.name === 'RangeError' && (!filename || filename === 'undefined')) {
        return 'reconciler_range'
    }

    return null
}

const resolveReasonType = (value: unknown): string => {
    if (value === null) return 'null'
    if (value instanceof Error) return 'error'
    if (Array.isArray(value)) return 'array'
    return typeof value
}

const resolveReasonName = (value: unknown): string | null => {
    if (value instanceof Error) return value.name || 'Error'
    if (value && typeof value === 'object') {
        const ctorName = (value as {constructor?: {name?: unknown}}).constructor?.name
        return typeof ctorName === 'string' && ctorName.trim() ? ctorName : 'Object'
    }
    return null
}

const resolveReasonCode = (value: unknown): string | number | null => {
    if (!value || typeof value !== 'object') return null
    const code = (value as {code?: unknown}).code
    if (typeof code === 'string' || typeof code === 'number') return code
    return null
}

const resolveReasonPreview = (value: unknown): string | null => {
    if (value instanceof Error) {
        const text = value.message || ''
        if (text) return sanitizeClientDiagnosticText(text)
        return '[error]'
    }
    if (typeof value === 'string') return sanitizeClientDiagnosticText(value)
    if (typeof value === 'number') return '[number]'
    if (typeof value === 'boolean') return '[boolean]'
    if (typeof value === 'bigint') return `[bigint digits=${value.toString().replace('-', '').length}]`
    if (typeof value === 'symbol') return '[symbol]'
    if (typeof value === 'function') return '[function]'
    if (value === null) return 'null'
    if (typeof value === 'undefined') return 'undefined'
    if (Array.isArray(value)) return `[array length=${value.length}]`
    if (typeof value === 'object') {
        const ctorName = (value as {constructor?: {name?: unknown}}).constructor?.name
        return `[object ${typeof ctorName === 'string' && ctorName.trim() ? ctorName : 'Object'}]`
    }
    return null
}

const buildRejectionContext = (value: unknown): RejectionContext => {
    const reasonIsError = value instanceof Error
    return {
        reasonType: resolveReasonType(value),
        reasonName: resolveReasonName(value),
        reasonCode: resolveReasonCode(value),
        reasonPreview: resolveReasonPreview(value),
        reasonIsError,
        reasonHasStack: reasonIsError && typeof value.stack === 'string' && value.stack.length > 0,
    }
}

const buildUnhandledRejectionMessage = (context: RejectionContext): string => {
    const base = 'Unhandled promise rejection'
    if (context.reasonIsError) {
        const preview = context.reasonPreview || '[error]'
        return `${base} (${context.reasonType}): ${preview}`
    }
    if (context.reasonPreview) {
        return `${base} (${context.reasonType}): ${context.reasonPreview}`
    }
    return `${base} (${context.reasonType})`
}

const describeNonError = (value: unknown): string => {
    const preview = resolveReasonPreview(value)
    if (preview) return preview
    return resolveReasonType(value)
}

const buildWindowErrorPreview = (event: ErrorEvent): string | null => {
    const fallbackErrorMessage = event.error instanceof Error ? event.error.message : ''
    const rawMessage = typeof event.message === 'string' && event.message.trim().length > 0
        ? event.message
        : fallbackErrorMessage
    if (!rawMessage) return null
    return resolveReasonPreview(rawMessage)
}

const buildWindowError = (event: ErrorEvent, preview: string | null): Error => {
    const sourceError = event.error instanceof Error ? event.error : null
    if (sourceError) {
        return sourceError
    }

    const safeMessage = preview ? `Window error captured: ${preview}` : 'Window error captured'
    const sanitizedError = new Error(safeMessage)
    sanitizedError.name = 'WindowError'
    sanitizedError.stack = undefined

    return sanitizedError
}

const coerceUnhandledRejectionError = (value: unknown): Error => {
    if (value instanceof Error) {
        return value
    }
    return new Error(`Non-error rejection: ${describeNonError(value)}`)
}

type BrowserConnection = {
    effectiveType?: string
    downlink?: number
    rtt?: number
    saveData?: boolean
}

type LastUiActivity = {
    eventType: string
    target: string | null
    targetText: string | null
    route: string
    scrollX: number | null
    scrollY: number | null
    occurredAt: number
}

type LastLongTask = {
    durationMs: number
    startTimeMs: number
    observedAt: number
}

type ResumeSignal = {
    occurredAt: number
    trigger: string
    hiddenDurationMs: number | null
    consumed: boolean
}

const BROWSER_HEARTBEAT_INTERVAL_MS = 60_000
const BROWSER_HEARTBEAT_LOG_EVERY_TICKS = 5
const BROWSER_STALL_THRESHOLD_MS = 2_000
const BROWSER_STALL_RATE_LIMIT_MS = 60_000
const BROWSER_RESUME_CLASSIFICATION_WINDOW_MS = BROWSER_HEARTBEAT_INTERVAL_MS + 30_000
const SLOW_RENDER_THRESHOLD_MS = 500
const LONG_TASK_RENDER_CAUSE_WINDOW_MS = 2_000
const LONG_TASK_RENDER_CAUSE_MIN_MS = 100
let lastUiActivity: LastUiActivity | null = null
let lastLongTask: LastLongTask | null = null

const resolveCurrentRoute = (): string => {
    if (typeof window === 'undefined') return '/'
    return window.location.pathname || '/'
}

const resolveViewport = () => {
    if (typeof window === 'undefined') {
        return {
            viewportWidth: null,
            viewportHeight: null,
            visualViewportWidth: null,
            visualViewportHeight: null,
            visualViewportOffsetTop: null,
        }
    }

    return {
        viewportWidth: Number.isFinite(window.innerWidth) ? Math.trunc(window.innerWidth) : null,
        viewportHeight: Number.isFinite(window.innerHeight) ? Math.trunc(window.innerHeight) : null,
        visualViewportWidth: window.visualViewport && Number.isFinite(window.visualViewport.width)
            ? Math.trunc(window.visualViewport.width)
            : null,
        visualViewportHeight: window.visualViewport && Number.isFinite(window.visualViewport.height)
            ? Math.trunc(window.visualViewport.height)
            : null,
        visualViewportOffsetTop: window.visualViewport && Number.isFinite(window.visualViewport.offsetTop)
            ? Math.trunc(window.visualViewport.offsetTop)
            : null,
    }
}

const describeElement = (target: EventTarget | null): string | null => {
    if (!(target instanceof HTMLElement)) return null

    const parts = [target.tagName.toLowerCase()]
    const role = target.getAttribute('role')
    const type = target.getAttribute('type')
    const name = target.getAttribute('name')
    const id = target.id
    const testId = target.getAttribute('data-testid')
    const href = target instanceof HTMLAnchorElement ? target.pathname : null

    if (role) parts.push(`role=${role}`)
    if (type) parts.push(`type=${type}`)
    if (name) parts.push(`name=${name}`)
    if (id) parts.push(`id=${id}`)
    if (testId) parts.push(`testid=${testId}`)
    if (href) parts.push(`href=${href}`)

    return parts.join(' ')
}

const resolveElementLabel = (target: EventTarget | null): string | null => {
    if (!(target instanceof HTMLElement)) return null
    const label = target.getAttribute('aria-label')
        || target.getAttribute('title')
        || (target instanceof HTMLButtonElement || target instanceof HTMLAnchorElement ? target.innerText : '')
    const normalized = label.replace(/\s+/g, ' ').trim()
    return normalized ? normalized.slice(0, 160) : null
}

const rememberUiActivity = (event: Event) => {
    if (typeof window === 'undefined') return
    lastUiActivity = {
        eventType: event.type,
        target: describeElement(event.target),
        targetText: resolveElementLabel(event.target),
        route: resolveCurrentRoute(),
        scrollX: Number.isFinite(window.scrollX) ? Math.trunc(window.scrollX) : null,
        scrollY: Number.isFinite(window.scrollY) ? Math.trunc(window.scrollY) : null,
        occurredAt: Date.now(),
    }
}

const resolveUiActivityContext = (): Record<string, string | number | null> => {
    const now = Date.now()
    return {
        lastActivityType: lastUiActivity?.eventType ?? null,
        lastActivityTarget: lastUiActivity?.target ?? null,
        lastActivityText: lastUiActivity?.targetText ?? null,
        lastActivityRoute: lastUiActivity?.route ?? null,
        lastActivityAgeMs: lastUiActivity ? Math.max(0, now - lastUiActivity.occurredAt) : null,
        lastActivityScrollX: lastUiActivity?.scrollX ?? null,
        lastActivityScrollY: lastUiActivity?.scrollY ?? null,
        lastLongTaskDurationMs: lastLongTask?.durationMs ?? null,
        lastLongTaskStartTimeMs: lastLongTask?.startTimeMs ?? null,
        lastLongTaskAgeMs: lastLongTask ? Math.max(0, now - lastLongTask.observedAt) : null,
    }
}

const resolveConnection = (): BrowserConnection => {
    if (typeof navigator === 'undefined') return {}
    const connection = (navigator as Navigator & {connection?: BrowserConnection}).connection
    if (!connection) return {}
    return {
        effectiveType: typeof connection.effectiveType === 'string' ? connection.effectiveType : undefined,
        downlink: typeof connection.downlink === 'number' && Number.isFinite(connection.downlink) ? connection.downlink : undefined,
        rtt: typeof connection.rtt === 'number' && Number.isFinite(connection.rtt) ? connection.rtt : undefined,
        saveData: typeof connection.saveData === 'boolean' ? connection.saveData : undefined,
    }
}

const buildBrowserHealthContext = (extra: Record<string, string | number | boolean | null> = {}) => {
    const nav = typeof navigator !== 'undefined'
        ? navigator as Navigator & {deviceMemory?: number}
        : null
    const connection = resolveConnection()

    return {
        route: resolveCurrentRoute(),
        pageVisibility: resolvePageVisibility(),
        isOnline: resolveOnlineStatus(),
        touchPoints: nav && typeof nav.maxTouchPoints === 'number' ? Math.trunc(nav.maxTouchPoints) : null,
        hardwareConcurrency: nav && typeof nav.hardwareConcurrency === 'number' ? Math.trunc(nav.hardwareConcurrency) : null,
        deviceMemoryGb: nav && typeof nav.deviceMemory === 'number' ? nav.deviceMemory : null,
        connectionEffectiveType: connection.effectiveType ?? null,
        connectionDownlinkMbps: connection.downlink ?? null,
        connectionRttMs: connection.rtt ?? null,
        connectionSaveData: connection.saveData ?? null,
        ...resolveViewport(),
        ...resolveUiActivityContext(),
        ...buildSharedClientContext(),
        ...extra,
    }
}

const resolveSlowRenderCauseHint = (context: Record<string, string | number | boolean | null>): string => {
    const renderDurationMs = typeof context.renderDurationMs === 'number' ? context.renderDurationMs : null
    if (renderDurationMs === null || renderDurationMs < SLOW_RENDER_THRESHOLD_MS) return 'within_threshold'

    const lastLongTaskDurationMs = typeof context.lastLongTaskDurationMs === 'number' ? context.lastLongTaskDurationMs : null
    const lastLongTaskAgeMs = typeof context.lastLongTaskAgeMs === 'number' ? context.lastLongTaskAgeMs : null
    if (
        lastLongTaskDurationMs !== null &&
        lastLongTaskDurationMs >= LONG_TASK_RENDER_CAUSE_MIN_MS &&
        lastLongTaskAgeMs !== null &&
        lastLongTaskAgeMs <= LONG_TASK_RENDER_CAUSE_WINDOW_MS
    ) {
        return 'long_task_near_render'
    }

    const activeDialogCount = typeof context.activeDialogCount === 'number' ? context.activeDialogCount : 0
    if (context.bodyNoScroll === true || context.htmlNoScroll === true || activeDialogCount > 0) {
        return 'modal_or_scroll_lock_active'
    }

    const deviceMemoryGb = typeof context.deviceMemoryGb === 'number' ? context.deviceMemoryGb : null
    const hardwareConcurrency = typeof context.hardwareConcurrency === 'number' ? context.hardwareConcurrency : null
    if (
        (deviceMemoryGb !== null && deviceMemoryGb <= 4) ||
        (hardwareConcurrency !== null && hardwareConcurrency <= 4)
    ) {
        return 'constrained_device'
    }

    const connectionEffectiveType = typeof context.connectionEffectiveType === 'string'
        ? context.connectionEffectiveType
        : null
    const connectionRttMs = typeof context.connectionRttMs === 'number' ? context.connectionRttMs : null
    if (
        connectionEffectiveType === 'slow-2g' ||
        connectionEffectiveType === '2g' ||
        (connectionRttMs !== null && connectionRttMs >= 500)
    ) {
        return 'slow_connection'
    }

    if (context.pageVisibility === 'hidden') return 'hidden_page'
    return 'unknown'
}

const enrichRenderTimingContext = (context: Record<string, string | number | boolean | null>) => {
    const renderDurationMs = typeof context.renderDurationMs === 'number' ? context.renderDurationMs : null
    return {
        ...context,
        slowRender: renderDurationMs !== null && renderDurationMs >= SLOW_RENDER_THRESHOLD_MS,
        slowRenderThresholdMs: SLOW_RENDER_THRESHOLD_MS,
        renderCauseHint: resolveSlowRenderCauseHint(context),
    }
}

export function GlobalErrorHandlers() {
    const renderTimingStartRef = useRef(getRenderTimingNow())
    const renderTimingLoggedRef = useRef(false)
    const {userId, orgId, sessionId, isLoaded, isSignedIn} = useAuth()
    const authDiagnostics = useMemo(
        () => buildAuthDiagnostics({userId, orgId, sessionId, isLoaded, isSignedIn}),
        [userId, orgId, sessionId, isLoaded, isSignedIn]
    )

    useEffect(() => {
        if (renderTimingLoggedRef.current) return
        renderTimingLoggedRef.current = true

        const startedAt = renderTimingStartRef.current
        const reportTiming = () => {
            const context = buildBrowserHealthContext({
                component: 'AppShell',
                renderDurationMs: Math.max(0, Math.round(getRenderTimingNow() - startedAt)),
                renderMode: 'initial-page',
                ...authDiagnostics,
            })
            const enrichedContext = enrichRenderTimingContext(context)
            if (enrichedContext.slowRender !== true) return

            logClientWarn({
                event: 'ui_render_timing',
                source: 'browser-render-monitor',
                message: 'Initial app render timing captured.',
                tags: ['ui-diagnostics', 'performance'],
                context: enrichedContext,
            })
        }

        if (typeof window.requestAnimationFrame === 'function') {
            const frameId = window.requestAnimationFrame(reportTiming)
            return () => window.cancelAnimationFrame(frameId)
        }

        const timeoutId = window.setTimeout(reportTiming, 0)
        return () => window.clearTimeout(timeoutId)
    }, [authDiagnostics])

    useEffect(() => {
        const activityEvents = ['pointerdown', 'click', 'submit', 'input', 'change', 'keydown'] as const
        for (const eventName of activityEvents) {
            document.addEventListener(eventName, rememberUiActivity, {capture: true, passive: true})
        }

        let longTaskObserver: PerformanceObserver | null = null
        try {
            if (typeof PerformanceObserver !== 'undefined') {
                longTaskObserver = new PerformanceObserver((list) => {
                    const entries = list.getEntries()
                    const last = entries[entries.length - 1]
                    if (!last) return
                    lastLongTask = {
                        durationMs: Math.round(last.duration),
                        startTimeMs: Math.round(last.startTime),
                        observedAt: Date.now(),
                    }
                })
                longTaskObserver.observe({type: 'longtask', buffered: true})
            }
        } catch {
            longTaskObserver = null
        }

        const handleWindowError = (event: ErrorEvent) => {
            const windowErrorPreview = buildWindowErrorPreview(event)
            const error = buildWindowError(event, windowErrorPreview)
            const domMutationSignal = resolveDomMutationSignal(error, event.filename || null)
            captureException(error, {
                event: 'window_error',
                source: 'window.onerror',
                message: windowErrorPreview ? `Window error (${windowErrorPreview})` : 'Window error',
                tags: domMutationSignal ? ['dom_mutation_suspected'] : undefined,
                context: {
                    filename: event.filename || null,
                    line: event.lineno,
                    column: event.colno,
                    errorMessagePreview: windowErrorPreview,
                    errorName: event.error instanceof Error ? event.error.name || 'Error' : null,
                    errorHasStack: event.error instanceof Error && typeof event.error.stack === 'string' && event.error.stack.length > 0,
                    hasErrorObject: event.error instanceof Error,
                    dom_mutation_signal: domMutationSignal,
                    pageVisibility: resolvePageVisibility(),
                    isOnline: resolveOnlineStatus(),
                    ...authDiagnostics,
                    ...buildSharedClientContext(),
                },
            })
        }

        const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
            const error = coerceUnhandledRejectionError(event.reason)
            const rejectionContext = buildRejectionContext(event.reason)
            const domMutationSignal = resolveDomMutationSignal(error)
            captureException(error, {
                event: 'unhandled_rejection',
                source: 'window.onunhandledrejection',
                message: buildUnhandledRejectionMessage(rejectionContext),
                tags: domMutationSignal ? ['dom_mutation_suspected'] : undefined,
                context: {
                    ...rejectionContext,
                    dom_mutation_signal: domMutationSignal,
                    pageVisibility: resolvePageVisibility(),
                    isOnline: resolveOnlineStatus(),
                    ...authDiagnostics,
                    ...buildSharedClientContext(),
                },
            })
        }

        window.addEventListener('error', handleWindowError)
        window.addEventListener('unhandledrejection', handleUnhandledRejection)

        return () => {
            for (const eventName of activityEvents) {
                document.removeEventListener(eventName, rememberUiActivity, {capture: true})
            }
            longTaskObserver?.disconnect()
            window.removeEventListener('error', handleWindowError)
            window.removeEventListener('unhandledrejection', handleUnhandledRejection)
        }
    }, [authDiagnostics])

    useEffect(() => {
        const startedAt = Date.now()
        let expectedAt = startedAt + BROWSER_HEARTBEAT_INTERVAL_MS
        let heartbeatTick = 0
        let lastStallLoggedAt = 0
        let lastHiddenAt: number | null = resolvePageVisibility() === 'hidden' ? startedAt : null
        let resumeSignal: ResumeSignal | null = null

        const markResume = (trigger: string) => {
            const now = Date.now()
            const hiddenDurationMs = lastHiddenAt === null ? null : Math.max(0, now - lastHiddenAt)
            if (hiddenDurationMs === null && resumeSignal && !resumeSignal.consumed) return
            resumeSignal = {
                occurredAt: now,
                trigger,
                hiddenDurationMs,
                consumed: false,
            }
            lastHiddenAt = null
        }

        const shouldReportResumeInsteadOfStall = (now: number, driftMs: number) => {
            if (!resumeSignal || resumeSignal.consumed) return false
            if (resumeSignal.hiddenDurationMs === null) return false
            if (driftMs < BROWSER_STALL_THRESHOLD_MS) return false
            if (now - resumeSignal.occurredAt > BROWSER_RESUME_CLASSIFICATION_WINDOW_MS) return false
            return true
        }

        const reportResumeAfterSuspension = (now: number, driftMs: number) => {
            const signal = resumeSignal
            resumeSignal = resumeSignal ? {...resumeSignal, consumed: true} : null
            logClientWarn({
                event: 'ui_resumed_after_suspension',
                source: 'browser-health-monitor',
                message: 'Browser resumed after a delayed heartbeat.',
                tags: ['ui-diagnostics', 'page-lifecycle'],
                context: buildBrowserHealthContext({
                    uptimeMs: Math.max(0, now - startedAt),
                    driftMs,
                    intervalMs: BROWSER_HEARTBEAT_INTERVAL_MS,
                    resumeTrigger: signal?.trigger ?? null,
                    resumeHiddenDurationMs: signal?.hiddenDurationMs ?? null,
                    resumeAgeMs: signal ? Math.max(0, now - signal.occurredAt) : null,
                    ...authDiagnostics,
                }),
            })
        }

        const classifyVisibleDelay = (now: number, driftMs: number): boolean => {
            if (resolvePageVisibility() !== 'visible' || driftMs < BROWSER_STALL_THRESHOLD_MS) {
                return true
            }
            if (shouldReportResumeInsteadOfStall(now, driftMs)) {
                reportResumeAfterSuspension(now, driftMs)
                return true
            }
            return false
        }

        const intervalId = window.setInterval(() => {
            const now = Date.now()
            const driftMs = Math.max(0, now - expectedAt)
            expectedAt = now + BROWSER_HEARTBEAT_INTERVAL_MS
            heartbeatTick += 1

            const uptimeMs = Math.max(0, now - startedAt)
            if ((heartbeatTick - 1) % BROWSER_HEARTBEAT_LOG_EVERY_TICKS === 0) {
                logClientWarn({
                    event: 'ui_heartbeat',
                    source: 'browser-health-monitor',
                    message: 'Browser UI heartbeat.',
                    tags: ['ui-diagnostics', 'heartbeat'],
                    context: buildBrowserHealthContext({
                        uptimeMs,
                        driftMs,
                        intervalMs: BROWSER_HEARTBEAT_INTERVAL_MS,
                        ...authDiagnostics,
                    }),
                })
            }

            if (classifyVisibleDelay(now, driftMs)) {
                return
            }

            if (now - lastStallLoggedAt >= BROWSER_STALL_RATE_LIMIT_MS) {
                lastStallLoggedAt = now
                logClientWarn({
                    event: 'ui_main_thread_stall',
                    source: 'browser-health-monitor',
                    message: 'Main thread was blocked long enough to affect UI responsiveness.',
                    tags: ['ui-diagnostics', 'performance'],
                    context: buildBrowserHealthContext({
                        uptimeMs,
                        driftMs,
                        intervalMs: BROWSER_HEARTBEAT_INTERVAL_MS,
                        ...authDiagnostics,
                    }),
                })
            }
        }, BROWSER_HEARTBEAT_INTERVAL_MS)

        const handleVisibilityChange = () => {
            if (resolvePageVisibility() === 'hidden') {
                lastHiddenAt = Date.now()
            } else if (resolvePageVisibility() === 'visible') {
                markResume('visibilitychange')
                const now = Date.now()
                const driftMs = Math.max(0, now - expectedAt)
                if (classifyVisibleDelay(now, driftMs)) {
                    expectedAt = now + BROWSER_HEARTBEAT_INTERVAL_MS
                }
            }
        }
        const handlePageHide = () => {
            lastHiddenAt = Date.now()
        }
        const handlePageShow = (event: PageTransitionEvent) => {
            markResume(event.persisted ? 'pageshow_bfcache' : 'pageshow')
        }
        const handleFocus = () => {
            if (resolvePageVisibility() === 'visible') {
                markResume('focus')
            }
        }

        document.addEventListener('visibilitychange', handleVisibilityChange)
        window.addEventListener('pagehide', handlePageHide)
        window.addEventListener('pageshow', handlePageShow)
        window.addEventListener('focus', handleFocus)

        return () => {
            window.clearInterval(intervalId)
            document.removeEventListener('visibilitychange', handleVisibilityChange)
            window.removeEventListener('pagehide', handlePageHide)
            window.removeEventListener('pageshow', handlePageShow)
            window.removeEventListener('focus', handleFocus)
        }
    }, [authDiagnostics])

    return null
}
